import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Tooltip,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { Edit, Save, Cancel, Preview, ExpandMore, Info } from '@mui/icons-material';
import { useEmailsettings } from '../hooks/useEmailSettings';
import { useThemes } from '../../../components/Theme/theme';
import { showSuccessSnackbar, showErrorSnackbar, showWarningSnackbar } from '../../../components/snackBar/snakbar';
import { TemplateUpdateConfirmationDialog, TemplateSaveOption } from './TemplateUpdateConfirmationDialog';

interface EmailTemplateData {
  templateid: number | string;
  emailbody: string;
  templatename?: string;
  [key: string]: any;
}

interface PlaceholderItem {
  name: string;
  description: string;
}

interface EmailTemplatePreviewProps {
  templateId?: number;
  onSaveTemplate?: (templateData: EmailTemplateData) => void;
  onTemplateSaveStatusChange?: (isSaved: boolean) => void;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
  onGetCurrentTemplateContent?: (getContent: () => string) => void;
  onGetSaveFunction?: (saveFunction: () => Promise<void>) => void;
  onGetUnsavedChangesCheck?: (checkFunction: () => boolean) => void;
  onGetShowUnsavedChangesPopup?: (showFunction: () => boolean) => void;
  onConfirmTemplateUpdate?: (option: TemplateSaveOption) => void;
  onCancelTemplateUpdate?: () => void;
  resetOnClose?: boolean;
  customContentForThisEmail?: string | null;
  onSetCustomContentForThisEmail?: (content: string | null) => void;
}

const DEFAULT_TEMPLATE_ID = 1;
const TEXTAREA_MIN_ROWS = 14;
const TEXTAREA_MAX_ROWS = 26;
const ACCORDION_MAX_HEIGHT = '180px';
const PLACEHOLDER_MAX_HEIGHT = '140px';

const EMAIL_PLACEHOLDERS: PlaceholderItem[] = [
  { name: '[Customer Name]', description: 'Customer or party name' },
  { name: '[Supplier Name]', description: 'Supplier or vendor name' },
  { name: '[partyName]', description: 'Party name' },
  { name: '[Invoice Number]', description: 'Invoice number' },
  { name: '[Invoice Date]', description: 'Invoice date' },
  { name: '[Purchase Order Number]', description: 'Purchase order number' },
  { name: '[Purchase Date]', description: 'Purchase date' },
  { name: '[Due Date]', description: 'Payment due date' },
  { name: '[Amount]', description: 'Invoice amount' },
  { name: '[debitNoteNumber]', description: 'Debit note number' },
  { name: '[debitNoteDate]', description: 'Debit note date' },
  { name: '[creditNoteNumber]', description: 'Credit note number' },
  { name: '[creditNoteDate]', description: 'Credit note date' },
  { name: '[Date From]', description: 'Start date for transaction summary' },
  { name: '[Date To]', description: 'End date for transaction summary' },
  { name: '[As of Date]', description: 'As of date for outstanding balance' },
  { name: '[Currency]', description: 'Currency symbol or code' },
  { name: '[Outstanding Amount]', description: 'Outstanding balance amount' },
  { name: '[Payment Link]', description: 'Payment link URL' },
  { name: '[Company Name]', description: 'Your company name' },
  { name: '[Company Email]', description: 'Your company email' },
  { name: '[Company Phone No]', description: 'Your company phone number' },
  { name: '[companyName]', description: 'Company name' },
  { name: '[companyEmail]', description: 'Company email' },
  { name: '[companyPhone]', description: 'Company phone' },
  { name: '[Your Name]', description: 'Your name' },
  { name: '[Your Position]', description: 'Your position/title' },
  { name: '[Email Address]', description: 'Your email address' },
  { name: '[Phone Number]', description: 'Your phone number' },
  { name: '[Payment Method]', description: 'Payment method' },
  { name: '[Payment Method(s)]', description: 'Available payment methods' },
  { name: '[Payment Amount]', description: 'Payment amount received' },
  { name: '[Payment Date]', description: 'Date of payment' },
  { name: '[Transaction ID]', description: 'Payment transaction ID' },
  { name: '[Reference Number]', description: 'Payment reference number' },
  { name: '[Website URL]', description: 'Company website' },
];

const MESSAGES = {
  SUCCESS: {
    SAVE: 'Email template saved successfully!',
    UPDATE: 'Email template updated successfully!',
    CANCEL: 'Template update cancelled',
  },
  ERROR: {
    SAVE_FAILED: 'Failed to save template changes',
  },
  WARNING: {
    NO_CHANGES: 'No changes detected. Please modify the template content before saving.',
  },
  INFO: {
    PLACEHOLDER_INSTRUCTION: 'Click on any placeholder to copy it to your clipboard:',
    PLACEHOLDER_DESCRIPTION: 'These placeholders will be automatically replaced with actual values when sending emails. Make sure to use the exact placeholder format shown above.',
  },
} as const;

export const EmailTemplatePreview: React.FC<EmailTemplatePreviewProps> = ({
  templateId = DEFAULT_TEMPLATE_ID,
  onSaveTemplate,
  onTemplateSaveStatusChange,
  onUnsavedChangesChange,
  onGetCurrentTemplateContent,
  onGetSaveFunction,
  onGetUnsavedChangesCheck,
  onGetShowUnsavedChangesPopup,
  onConfirmTemplateUpdate,
  onCancelTemplateUpdate,
  resetOnClose = false,
  customContentForThisEmail: parentCustomContent,
  onSetCustomContentForThisEmail,
}) => {


  const { theme } = useThemes();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [showUpdateConfirmation, setShowUpdateConfirmation] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<EmailTemplateData | null>(null);
  const [isLocallySaved, setIsLocallySaved] = useState(false);
  const [pendingSaveOption, setPendingSaveOption] = useState<TemplateSaveOption | null>(null);
  // Use parent's custom content if provided, otherwise use local state
  const [localCustomContent, setLocalCustomContent] = useState<string | null>(null);
  const customContentForThisEmail = parentCustomContent !== undefined ? parentCustomContent : localCustomContent;
  
  const textAreaRef = useRef<HTMLDivElement>(null);
  const hasInitializedEdit = useRef(false);
  const callbackRefs = useRef({
    onTemplateSaveStatusChange,
    onUnsavedChangesChange,
    onGetCurrentTemplateContent,
    onGetSaveFunction,
    onGetUnsavedChangesCheck,
    onGetShowUnsavedChangesPopup
  });

  useEffect(() => {
    callbackRefs.current = {
      onTemplateSaveStatusChange,
      onUnsavedChangesChange,
      onGetCurrentTemplateContent,
      onGetSaveFunction,
      onGetUnsavedChangesCheck,
      onGetShowUnsavedChangesPopup
    };
  }, [onTemplateSaveStatusChange, onUnsavedChangesChange, onGetCurrentTemplateContent, onGetSaveFunction, onGetUnsavedChangesCheck, onGetShowUnsavedChangesPopup]);

  const onSaveComplete = useCallback(() => {
    setIsEditing(false);
    showSuccessSnackbar(MESSAGES.SUCCESS.UPDATE);
  }, []);

  const {
    data,
    TemplateList,
    saveChanges,
    isEditEnabled,
    setIsEditEnabled,
    isTemplateSaved,
    loadingEMaster,
    loadingTList,
  } = useEmailsettings({
    select: templateId,
    onSaveComplete,
    parent: 'modal',
  });

  const isTemplateSavedMemo = useCallback((id: number) => {
    return isTemplateSaved(id);
  }, [isTemplateSaved]);

  const getTemplateName = useCallback((templateId: number) => {
    const template = TemplateList.find((t: any) => t.templateid === templateId);
    return template?.templatename || `Template ${templateId}`;
  }, [TemplateList]);

  const hasUnsavedChanges = useMemo(() => {
    return isEditing && editedContent !== (data?.emailbody || '');
  }, [isEditing, editedContent, data?.emailbody]);

  const isLoading = useMemo(() => {
    return loadingEMaster || loadingTList;
  }, [loadingEMaster, loadingTList]);

  useEffect(() => {
    const callbacks = callbackRefs.current;
    
    if (callbacks.onTemplateSaveStatusChange && data) {
      const saved = isTemplateSavedMemo(templateId);
      callbacks.onTemplateSaveStatusChange(saved);
    }

    if (callbacks.onUnsavedChangesChange) {
      callbacks.onUnsavedChangesChange(hasUnsavedChanges);
    }

    if (callbacks.onGetCurrentTemplateContent) {
      const getCurrentContent = () => {
        // If custom content for this email is set, return it
        // Otherwise return edited content if editing, or original template content
        return customContentForThisEmail || editedContent || (data?.emailbody || '');
      };
      callbacks.onGetCurrentTemplateContent(getCurrentContent);
    }

    if (callbacks.onGetSaveFunction) {
      const saveFunction = async () => {
        const hasChanges = editedContent !== (data?.emailbody || '');
        const isFirstSave = !isTemplateSavedMemo(templateId);
        
        if (isEditing && (isFirstSave || hasChanges)) {
          const updatedData: EmailTemplateData = {
            ...data,
            emailbody: editedContent,
          };
          
          await saveChanges(updatedData);
          
          if (onSaveTemplate) {
            onSaveTemplate(updatedData);
          }
          
          setIsEditing(false);
          setIsLocallySaved(true);
          showSuccessSnackbar(MESSAGES.SUCCESS.SAVE);
          
          if (onTemplateSaveStatusChange) {
            onTemplateSaveStatusChange(true);
          }
          
          if (onUnsavedChangesChange) {
            onUnsavedChangesChange(false);
          }
        }
      };
      callbacks.onGetSaveFunction(saveFunction);
    }

    if (callbacks.onGetUnsavedChangesCheck) {
      const checkUnsavedChanges = () => hasUnsavedChanges;
      callbacks.onGetUnsavedChangesCheck(checkUnsavedChanges);
    }

    if (callbacks.onGetShowUnsavedChangesPopup) {
      const showUnsavedChangesPopup = () => {
        if (hasUnsavedChanges) {
          const updatedData: EmailTemplateData = {
            ...data,
            emailbody: editedContent,
          };
          setPendingSaveData(updatedData);
          setShowUpdateConfirmation(true);
          return true;
        }
        return false;
      };
      callbacks.onGetShowUnsavedChangesPopup(showUnsavedChangesPopup);
    }
  }, [
    data, 
    templateId, 
    isTemplateSavedMemo, 
    hasUnsavedChanges, 
    isEditing, 
    editedContent,
    customContentForThisEmail,
    onTemplateSaveStatusChange,
    onUnsavedChangesChange,
    onGetCurrentTemplateContent,
    onGetSaveFunction,
    onGetUnsavedChangesCheck,
    onGetShowUnsavedChangesPopup
  ]);


  const handleEditClick = useCallback(() => {
    setIsEditing(true);
    // Reset custom content when editing again - use original template
    if (onSetCustomContentForThisEmail) {
      onSetCustomContentForThisEmail(null);
    } else {
      setLocalCustomContent(null);
    }
    setEditedContent(data?.emailbody || '');
    setIsEditEnabled(true);
    setIsLocallySaved(false);
  }, [data?.emailbody, onSetCustomContentForThisEmail]);

  const handleSaveClick = useCallback(async () => {
    try {
      const hasChanges = editedContent !== (data?.emailbody || '');
      
      if (!hasChanges) {
        showWarningSnackbar(MESSAGES.WARNING.NO_CHANGES);
        return;
      }
      
      const updatedData: EmailTemplateData = {
        ...data,
        emailbody: editedContent,
      };
      
      // Always show confirmation dialog when there are changes
      setPendingSaveData(updatedData);
      setShowUpdateConfirmation(true);
    } catch (error) {
      showErrorSnackbar(MESSAGES.ERROR.SAVE_FAILED);
    }
  }, [editedContent, data, onSaveTemplate, onTemplateSaveStatusChange, onUnsavedChangesChange]);

  const handleCancelClick = useCallback(() => {
    setIsEditing(false);
    setIsEditEnabled(false);
  }, []);

  const handleConfirmUpdate = useCallback(async (option: TemplateSaveOption) => {
    try {
      if (pendingSaveData) {
        setPendingSaveOption(option);
        
        if (option === 'save_permanently') {
          // Save template permanently
          await saveChanges(pendingSaveData);
          
          if (onSaveTemplate) {
            onSaveTemplate(pendingSaveData);
          }
          
          setIsEditing(false);
          setIsLocallySaved(true);
          showSuccessSnackbar(MESSAGES.SUCCESS.UPDATE);
          
          if (onTemplateSaveStatusChange) {
            onTemplateSaveStatusChange(true);
          }
          
          if (onUnsavedChangesChange) {
            onUnsavedChangesChange(false);
          }
        } else if (option === 'this_email_only') {
          // Use custom content for this email only, don't save template
          // Preserve the edited content for display but don't save to template
          // Use pendingSaveData.emailbody which contains the latest edited content
          const customContent = pendingSaveData.emailbody || editedContent;
          // Update parent state if callback provided, otherwise use local state
          if (onSetCustomContentForThisEmail) {
            onSetCustomContentForThisEmail(customContent);
          } else {
            setLocalCustomContent(customContent);
          }
          // Also update editedContent to ensure consistency when switching tabs
          setEditedContent(customContent);
          setIsEditing(false);
          setIsLocallySaved(true);
          hasInitializedEdit.current = true; // Prevent reset on next render
          showSuccessSnackbar('Template content will be used for this email only');
          
          if (onUnsavedChangesChange) {
            onUnsavedChangesChange(false);
          }
        } else if (option === 'discard') {
          // Discard changes
          setIsEditing(false);
          setEditedContent(data?.emailbody || '');
          // Clear custom content in parent if callback provided
          if (onSetCustomContentForThisEmail) {
            onSetCustomContentForThisEmail(null);
          } else {
            setLocalCustomContent(null);
          }
          showSuccessSnackbar(MESSAGES.SUCCESS.CANCEL);
          
          if (onUnsavedChangesChange) {
            onUnsavedChangesChange(false);
          }
          
          if (onCancelTemplateUpdate) {
            onCancelTemplateUpdate();
          }
        }
        
        if (onConfirmTemplateUpdate) {
          onConfirmTemplateUpdate(option);
        }
      }
    } catch (error) {
      showErrorSnackbar(MESSAGES.ERROR.SAVE_FAILED);
    } finally {
      setShowUpdateConfirmation(false);
      setPendingSaveData(null);
      setPendingSaveOption(null);
    }
  }, [pendingSaveData, editedContent, data, saveChanges, onSaveTemplate, onTemplateSaveStatusChange, onUnsavedChangesChange, onConfirmTemplateUpdate, onCancelTemplateUpdate, onSetCustomContentForThisEmail]);

  const handleCancelUpdate = useCallback(() => {
    setShowUpdateConfirmation(false);
    setPendingSaveData(null);
    setPendingSaveOption(null);
  }, []);

  const handleCloseConfirmation = useCallback(() => {
    setShowUpdateConfirmation(false);
    setPendingSaveData(null);
  }, []);


  const handlePlaceholderClick = useCallback((placeholderName: string) => {
    navigator.clipboard.writeText(placeholderName);
  }, []);

  // Load template content when data is available
  // Don't reset if customContentForThisEmail is set (user chose "This Email Only")
  useEffect(() => {
    if (data && !isEditing && !hasInitializedEdit.current) {
      // If custom content exists, use it; otherwise use template content
      const templateContent = customContentForThisEmail || data?.emailbody || '';
      if (templateContent) {
        hasInitializedEdit.current = true;
        setEditedContent(templateContent);
      }
    }
  }, [data, isEditing, customContentForThisEmail]);

  // Reset custom content when modal closes
  useEffect(() => {
    if (resetOnClose) {
      if (onSetCustomContentForThisEmail) {
        onSetCustomContentForThisEmail(null);
      } else {
        setLocalCustomContent(null);
      }
      setEditedContent(data?.emailbody || '');
      setIsEditing(false);
      setIsLocallySaved(false);
      hasInitializedEdit.current = false;
    }
  }, [resetOnClose, data?.emailbody, onSetCustomContentForThisEmail]);

  const LoadingSkeleton = useMemo(() => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 2, 
      p: 2,
      height: '100%',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress 
            size={20} 
            sx={{ 
              color: 'primary.main',
              mr: 1,
            }} 
          />
          <Skeleton variant="text" width={200} height={32} />
        </Box>
        <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: 1 }} />
      </Box>
      
      <Divider />
      
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {Array.from({ length: 12 }).map((_, index) => (
          <Skeleton key={index} variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
        ))}
      </Box>
      
      <Box sx={{ mt: 1 }}>
        <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: 1 }} />
      </Box>
    </Box>
  ), []);

  const LoadingSkeletonComponent = () => LoadingSkeleton;

  const isDarkTheme = useMemo(() => 
    (theme?.palette as any)?.TmId?.id?.toString().includes('9'), 
    [theme]
  );

  const paperStyles = useMemo(() => ({
    p: { xs: 1, sm: 1.5 },
    borderRadius: 2,
    backgroundColor: isDarkTheme 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'background.paper',
    border: `1px solid ${theme?.palette?.divider}`,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  }), [isDarkTheme, theme?.palette?.divider]);

  const textFieldStyles = useMemo(() => ({
    flex: 1,
    '& .MuiOutlinedInput-root': {
      backgroundColor: isDarkTheme 
        ? 'rgba(255, 255, 255, 0.08)' 
        : 'white',
      borderRadius: 1,
      height: '100%',
      border: isEditing ? undefined : `2px solid ${theme?.palette?.success.main}20`,
      '& fieldset': {
        borderColor: isEditing ? theme?.palette?.divider : theme?.palette?.success.main,
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: theme?.palette?.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme?.palette?.primary.main,
        borderWidth: '2px',
      },
    },
    '& .MuiInputBase-input': {
      fontSize: { xs: '14px', sm: '16px' },
      lineHeight: 1.6,
      color: isDarkTheme ? 'white' : 'text.primary',
      height: '100% !important',
      overflow: 'auto !important',
      fontWeight: isEditing ? 'normal' : 500,
      opacity: 1,
    },
  }), [isDarkTheme, theme, isEditing]);

  if (isLoading) {
    return (
      <Paper elevation={2} sx={paperStyles}>
        <LoadingSkeletonComponent />
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={paperStyles}>
      {/* Enhanced Title Section */}
      <Box 
        sx={{ 
          mb: 1.5,
          pb: 1.5,
          borderBottom: '2px solid',
          borderColor: 'divider',
          background: 'linear-gradient(to right, rgba(145, 27, 124, 0.03), transparent)',
          borderRadius: 1,
          px: 1,
          pt: 1,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #911B7C 0%, #6B1460 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(145, 27, 124, 0.3)',
              }}
            >
              <Preview sx={{ fontSize: 23, color: 'white' }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: isDarkTheme ? 'white' : 'text.primary',
                  fontSize: { xs: '12px', sm: '15px', md: '18px' },
                  mb: 0.25,
                  ...(!isDarkTheme && {
                    background: 'linear-gradient(135deg, #911B7C 0%, #6B1460 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }),
                }}
              >
                {getTemplateName(templateId)} - Email Template Preview
              </Typography>
            </Box>
          </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isEditing ? (
            <>
              <Button
                variant="outlined"
                onClick={handleCancelClick}
                startIcon={<Cancel />}
                size="small"
                disabled={!isTemplateSavedMemo(templateId)}
                sx={{
                  color: (theme?.palette as any)?.TmId?.id?.toString().includes('9') ? 'white' : 'text.primary',
                  borderColor: theme?.palette?.divider,
                  backgroundColor: (theme?.palette as any)?.TmId?.id?.toString().includes('9')
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.02)',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: { xs: '12px', sm: '13px' },
                  px: { xs: 1.5, sm: 2 },
                  py: 0.5,
                  minWidth: 'auto',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: (theme?.palette as any)?.TmId?.id?.toString().includes('9')
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.05)',
                    borderColor: theme?.palette?.text.secondary,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveClick}
                startIcon={<Save />}
                size="small"
                sx={{
                  backgroundColor: '#ff6600',
                  color: 'white',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: { xs: '12px', sm: '13px' },
                  px: { xs: 1.5, sm: 2 },
                  py: 0.5,
                  minWidth: 'auto',
                  boxShadow: '0 2px 4px rgba(255, 102, 0, 0.3)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: '#e55a00',
                    boxShadow: '0 4px 8px rgba(255, 102, 0, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: '0 2px 4px rgba(255, 102, 0, 0.3)',
                  },
                }}
              >
                Save
              </Button>
            </>
          ) : (
            <Tooltip title="Edit Template" arrow>
              <Button
                variant="outlined"
                onClick={handleEditClick}
                startIcon={<Edit />}
                size="small"
                sx={{
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  backgroundColor: (theme?.palette as any)?.TmId?.id?.toString().includes('9')
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(25, 118, 210, 0.04)',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: { xs: '12px', sm: '13px' },
                  px: { xs: 1.5, sm: 2 },
                  py: 0.5,
                  minWidth: 'auto',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    borderColor: 'primary.main',
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                Edit
              </Button>
            </Tooltip>
          )}
        </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 0.5 }} />

      <Box sx={{ 
        mb: 0,
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0,
        maxHeight: 'calc(100% - 60px)',
        overflow: 'hidden',
        position: 'relative',
      }}>
        
        {isEditing ? (
          <TextField
            ref={textAreaRef}
            multiline
            fullWidth
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            minRows={TEXTAREA_MIN_ROWS}
            maxRows={TEXTAREA_MAX_ROWS}
            placeholder="Enter your email template content here..."
            sx={textFieldStyles}
            InputProps={{
              classes: {
                input: 'custom-scrollbar',
              },
            }}
          />
        ) : (
          <TextField
            multiline
            fullWidth
            value={customContentForThisEmail || data?.emailbody || ''}
            sx={textFieldStyles}
            InputProps={{
              readOnly: true,
              classes: {
                input: 'custom-scrollbar',
              },
            }}
          />
        )}
      </Box>

      <Accordion 
        sx={{ 
          mt: 0.25,
          mb: 0,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '4px 0 0 0',
          },
          '& .MuiAccordionSummary-root': {
            '&.Mui-expanded': {
              minHeight: 'auto',
            },
          },
          '& .MuiAccordionDetails-root': {
            paddingTop: 0,
          },
        }} 
        defaultExpanded={false}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: isDarkTheme 
              ? 'rgba(255, 255, 255, 0.08)' 
              : 'grey.100',
            borderRadius: '8px',
            border: `1px solid ${theme?.palette?.divider}`,
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              gap: 1,
              margin: '8px 0',
            },
            '&.Mui-expanded': {
              minHeight: 'auto',
              '& .MuiAccordionSummary-content': {
                margin: '8px 0',
              },
            },
            minHeight: { xs: 40, sm: 44 },
            '&:hover': {
              backgroundColor: isDarkTheme 
                ? 'rgba(255, 255, 255, 0.12)' 
                : 'grey.200',
            },
          }}
        >
          <Info sx={{ 
            fontSize: { xs: 16, sm: 18 },
            color: 'primary.main',
          }} />
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '13px', sm: '14px' },
              color: isDarkTheme ? 'white' : 'text.primary',
            }}
          >
            Available Placeholders
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ 
          p: { xs: 1, sm: 1.5 },
          backgroundColor: isDarkTheme 
            ? 'rgba(255, 255, 255, 0.02)' 
            : 'grey.50',
          borderRadius: '0 0 8px 8px',
          border: `1px solid ${theme?.palette?.divider}`,
          borderTop: 'none',
          maxHeight: ACCORDION_MAX_HEIGHT,
          overflow: 'auto',
        }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                mb: 1,
                color: isDarkTheme ? 'white' : 'text.secondary',
                fontSize: { xs: '11px', sm: '12px' },
                fontWeight: 500,
              }}
            >
              {MESSAGES.INFO.PLACEHOLDER_INSTRUCTION}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: { xs: 0.5, sm: 0.75 },
              justifyContent: { xs: 'center', sm: 'flex-start' },
              maxHeight: PLACEHOLDER_MAX_HEIGHT,
              overflow: 'auto',
              p: 1,
              backgroundColor: isDarkTheme 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'white',
              borderRadius: '6px',
              border: `1px solid ${theme?.palette?.divider}`,
            }}>
              {EMAIL_PLACEHOLDERS.map((placeholder, index) => (
                <Tooltip key={index} title={placeholder.description} arrow>
                  <Chip
                    label={placeholder.name}
                    size="small"
                    onClick={() => handlePlaceholderClick(placeholder.name)}
                    sx={{
                      backgroundColor: isDarkTheme 
                        ? 'rgba(255, 255, 255, 0.15)' 
                        : 'primary.light',
                      color: isDarkTheme 
                        ? 'white' 
                        : 'primary.contrastText',
                      fontSize: { xs: '10px', sm: '11px' },
                      height: { xs: 24, sm: 26 },
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '& .MuiChip-label': {
                        px: { xs: 0.75, sm: 1 },
                      },
                      '&:hover': {
                        backgroundColor: isDarkTheme 
                          ? 'rgba(255, 255, 255, 0.25)' 
                          : 'primary.main',
                        transform: 'scale(1.05)',
                      },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
          
          <Alert 
            severity="info" 
            sx={{ 
              backgroundColor: isDarkTheme 
                ? 'rgba(33, 150, 243, 0.15)' 
                : 'info.light',
              color: isDarkTheme ? 'white' : 'info.contrastText',
              fontSize: { xs: '11px', sm: '12px' },
              py: { xs: 0.75, sm: 1 },
              borderRadius: '6px',
              border: `1px solid ${theme?.palette?.info.main}`,
              '& .MuiAlert-icon': {
                fontSize: { xs: '16px', sm: '18px' },
              },
            }}
          >
            <Typography variant="caption" sx={{ fontSize: 'inherit', fontWeight: 500 }}>
              {MESSAGES.INFO.PLACEHOLDER_DESCRIPTION}
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      <TemplateUpdateConfirmationDialog
        open={showUpdateConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmUpdate}
        onCancel={handleCancelUpdate}
        templateName={getTemplateName(templateId)}
      />
    </Paper>
  );
};

export default EmailTemplatePreview;
